// === Admin: Surveillance Pests ===
// Full CRUD page — list, add, edit, soft-delete.
// Pests for the Pest & Disease Surveillance Data Sheet, each tagged with the
// crop types it applies to (shown in an expandable child row).

import { Fragment, useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Autocomplete,
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemText,
  createFilterOptions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { AppTableHead, AppTableHeadCell, AppTableRow, DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SurveillancePestRow {
  readonly id: number;
  readonly pest: string;
  readonly crop_types: readonly string[];
  readonly active: boolean;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

interface FormState {
  pest: string;
  crop_types: string[];
  active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

const DEFAULT_FORM: FormState = { pest: '', crop_types: [], active: true, created_at: null, updated_at: null };

// Sentinel option rendered as the "Select all" checkbox at the top of the crop types list.
const SELECT_ALL = '__select_all__';
const MAX_CROP_TYPE_CHIPS = 8;
const filterCropTypes = createFilterOptions<string>();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtAuDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'short', timeStyle: 'medium', hour12: false }).format(new Date(iso));
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function SurveillancePestsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // ---- List state ----
  const [rows, setRows] = useState<readonly SurveillancePestRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Expanded child rows (crop type lists) ----
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<number>>(() => new Set());
  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ---- Crop type options ----
  const [cropTypeOptions, setCropTypeOptions] = useState<readonly string[]>([]);
  const [cropTypesLoading, setCropTypesLoading] = useState(false);
  const [cropTypesError, setCropTypesError] = useState<string | null>(null);

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A saved crop type that has since been deactivated is still selectable, so
  // editing a pest never silently drops it.
  const cropTypeChoices = useMemo(
    () => [...new Set([...cropTypeOptions, ...form.crop_types])].sort((a, b) => a.localeCompare(b)),
    [cropTypeOptions, form.crop_types],
  );

  // Search text typed into Crop Types. Kept while ticking options so several
  // matches can be picked from one search; "Select all" acts on these matches.
  const [cropTypeInput, setCropTypeInput] = useState('');
  const matchingCropTypes = useMemo(
    () => filterCropTypes(cropTypeChoices, { inputValue: cropTypeInput, getOptionLabel: (option) => option }),
    [cropTypeChoices, cropTypeInput],
  );

  // ---- Delete dialog ----
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/surveillance-pests`);
      url.searchParams.set('page', String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);

      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: SurveillancePestRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load pests');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // ---------------------------------------------------------------------------
  // Fetch crop type options (once per session token)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setCropTypesLoading(true);
    setCropTypesError(null);
    void (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/v1/admin/surveillance-pests/crop-type-options`, {
          headers: authHeaders(token),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { data: string[] };
        if (!cancelled) setCropTypeOptions(json.data);
      } catch (e) {
        if (!cancelled) setCropTypesError(e instanceof Error ? e.message : 'Failed to load crop types');
      } finally {
        if (!cancelled) setCropTypesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // ---------------------------------------------------------------------------
  // Open Add / Edit
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setCropTypeInput('');
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    if (!token) return;
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/surveillance-pests/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: SurveillancePestRow };
      setForm({
        pest: json.data.pest,
        crop_types: [...json.data.crop_types],
        active: json.data.active,
        created_at: json.data.created_at ?? null,
        updated_at: json.data.updated_at ?? null,
      });
      setCropTypeInput('');
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const pest = form.pest.trim();
    if (!pest) {
      setFormError('Pest is required.');
      return;
    }
    if (form.crop_types.length === 0) {
      setFormError('Select at least one crop type.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const url = editingId
        ? `${getApiUrl()}/api/v1/admin/surveillance-pests/${editingId}`
        : `${getApiUrl()}/api/v1/admin/surveillance-pests`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ pest, crop_types: form.crop_types, active: form.active }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      showSnack(editingId ? 'Pest updated.' : 'Pest added.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/surveillance-pests/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Pest deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Surveillance</Typography>
            <Typography variant="body2" color="text.secondary">
              Pests and the crop types they apply to, for the Pest &amp; Disease Surveillance Data Sheet
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Pest
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by pest…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 260 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>Clear</Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell sx={{ width: 64 }} />
                  <AppTableHeadCell>Pest</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 120 }}>Active</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ width: 96 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const open = expandedIds.has(row.id);
                    return (
                      <Fragment key={row.id}>
                        <AppTableRow sx={open ? { '& > td': { borderBottom: 'none' } } : {}}>
                          <TableCell>
                            <Tooltip title={open ? 'Hide crop types' : 'Show crop types'}>
                              <IconButton size="small" onClick={() => toggleExpanded(row.id)} aria-expanded={open}>
                                {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{row.pest}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.active ? 'Active' : 'Inactive'}
                              size="small"
                              color={row.active ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => void openEdit(row.id)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => confirmDelete(row.id, row.pest)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </AppTableRow>

                        {/* Child row — simple list of the pest's crop types */}
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 0, ...(open ? {} : { borderBottom: 'none' }) }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1.5, pl: 8 }}>
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  color="text.secondary"
                                  sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}
                                >
                                  Crop Types ({row.crop_types.length})
                                </Typography>
                                {row.crop_types.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">No crop types selected</Typography>
                                ) : (
                                  <List dense disablePadding sx={{ columnCount: { xs: 2, sm: 3, md: 4 }, columnGap: 4, mt: 0.5 }}>
                                    {row.crop_types.map((cropType) => (
                                      <ListItem key={cropType} disableGutters sx={{ py: 0.25, display: 'block', breakInside: 'avoid' }}>
                                        <ListItemText primary={cropType} />
                                      </ListItem>
                                    ))}
                                  </List>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}>
              {loading ? 'Loading…' : total === 0 ? 'No records found' :
                `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`
              }
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>Show</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Pest' : 'Add Pest'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Pest"
            size="small"
            fullWidth
            required
            placeholder="e.g. Red Scale"
            inputProps={{ maxLength: 150 }}
            value={form.pest}
            onChange={(e) => setForm((f) => ({ ...f, pest: e.target.value }))}
          />
          {cropTypesError && (
            <Alert severity="warning">Couldn&apos;t load crop types: {cropTypesError}</Alert>
          )}
          {/* Searchable multi-select: type to filter, tick as many crop types as apply.
              The first option is "Select all" — it ticks (or clears) every crop type
              matching the current search. */}
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={cropTypeChoices}
            value={form.crop_types}
            inputValue={cropTypeInput}
            onInputChange={(_, value, reason) => {
              // Ignore the reset MUI does after each tick, so the search text stays.
              if (reason === 'input' || reason === 'clear') setCropTypeInput(value);
            }}
            onBlur={() => setCropTypeInput('')}
            filterOptions={(options, state) => {
              const matches = filterCropTypes(options, state);
              return matches.length > 0 ? [SELECT_ALL, ...matches] : matches;
            }}
            getOptionLabel={(option) => (option === SELECT_ALL ? 'Select all' : option)}
            onChange={(_, value) => {
              if (!value.includes(SELECT_ALL)) {
                setForm((f) => ({ ...f, crop_types: value }));
                return;
              }
              setForm((f) => {
                const allMatchingSelected = matchingCropTypes.every((cropType) => f.crop_types.includes(cropType));
                return {
                  ...f,
                  crop_types: allMatchingSelected
                    ? f.crop_types.filter((cropType) => !matchingCropTypes.includes(cropType))
                    : [...new Set([...f.crop_types, ...matchingCropTypes])],
                };
              });
            }}
            loading={cropTypesLoading}
            renderTags={(value, getTagProps) => [
              ...value.slice(0, MAX_CROP_TYPE_CHIPS).map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} size="small" label={option} {...tagProps} />;
              }),
              ...(value.length > MAX_CROP_TYPE_CHIPS
                ? [<Chip key="more" size="small" variant="outlined" label={`+${value.length - MAX_CROP_TYPE_CHIPS} more`} />]
                : []),
            ]}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              if (option === SELECT_ALL) {
                const selectedCount = matchingCropTypes.filter((cropType) => form.crop_types.includes(cropType)).length;
                const allSelected = selectedCount === matchingCropTypes.length;
                return (
                  <Box component="li" key={key} {...optionProps} sx={{ borderBottom: 1, borderColor: 'divider', fontWeight: 600 }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={selectedCount > 0 && !allSelected}
                      sx={{ mr: 1, p: 0.5 }}
                    />
                    {cropTypeInput.trim() ? 'Select all matching' : 'Select all'}
                  </Box>
                );
              }
              return (
                <li key={key} {...optionProps}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
                  {option}
                </li>
              );
            }}
            renderInput={(params) => (
              // @ts-expect-error: MUI Autocomplete renderInput params have optional props incompatible with exactOptionalPropertyTypes
              <TextField
                {...params}
                size="small"
                label="Crop Types"
                required
                placeholder={form.crop_types.length === 0 ? 'Search crop types…' : ''}
              />
            )}
          />
          <Select
            size="small"
            value={form.active ? 'true' : 'false'}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === 'true' }))}
            fullWidth
          >
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>

          {/* Audit Trail */}
          {editingId && (
            <Box
              mt={1}
              p={2}
              sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                Audit Trail
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtAuDate(form.created_at)}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtAuDate(form.updated_at)}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : undefined}
          >
            {editingId ? 'Save Changes' : 'Add Pest'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Pest</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete pest <strong>{deleteLabel}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
